import { useMemo } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { ERROR_CODES } from '@/services/apiError'
import {
  createNode,
  deleteNode,
  fetchAncestors,
  fetchChildCount,
  fetchChildren,
  fetchNode,
  updateNode,
} from './api'

/**
 * The editable asset hierarchy, live.
 *
 * Reads the flat node list rather than a nested tree: the tree is a derived
 * value and deriving it client-side keeps every mutation a single-node write
 * instead of a recursive patch.
 */

// ── `useHierarchyNodes`, RETIRED 2026-10-13 ──────────────────────────────
// It read `GET /assets/hierarchy` — the whole estate in one response — and every
// caller has moved to the per-level collections, as Settings → Sites & Hierarchy
// did: `HierarchyPicker`/`PlaceField` to choose a place, `usePlaceNodes` below
// to name the places a screen shows. Kept commented, per the standing recipe.
//
// /**
//  * `enabled` is the caller's, not this hook's.
//  *
//  * `/assets/hierarchy` returns the WHOLE estate in one response, so a screen that
//  * only needs it once a dialog opens should not pull it on mount — and a screen
//  * whose reader may not be entitled to it should not request it at all. Several
//  * callers want it unconditionally, which is why it defaults to true rather than
//  * being decided here.
//  */
// export function useHierarchyNodes({ enabled = true } = {}) {
//   return useQuery({
//     queryKey: queryKeys.hierarchyNodes,
//     queryFn: ({ signal }) => fetchHierarchyNodes(signal),
//     staleTime: 60_000,
//     enabled,
//   })
// }

/**
 * The nodes needed to NAME a handful of places — each one and its ancestors.
 *
 * What replaced the whole-tree read for screens that only display names: a
 * sensor list, a connection's site. Each target is resolved by walking up through
 * the per-tier DETAIL endpoints (`GET /assets/plants|lines|machines/{id}`), the
 * same records Sites & Hierarchy reads, and each node is cached on its own — so
 * twenty sensors on one line read that line and its plant once, not twenty times.
 *
 * The result has the old hook's shape, `{data: {items}}` of flat nodes with
 * `parentId`, so `describeParent`, `parentOf` and friends work on it unchanged.
 *
 * A 404 is an ANSWER, not a failure: deletion is soft and nothing cascades, so a
 * sensor can outlive its machine. Such a target resolves to nothing and the
 * caller draws its unresolved state. `resolving` holds the ids still in flight,
 * so a row can say "loading" rather than "deleted" while it waits.
 *
 * @param {Array<{tier: string, id: string}|null>} targets
 */
export function usePlaceNodes(targets, { enabled = true } = {}) {
  const client = useQueryClient()

  const unique = useMemo(() => {
    const seen = new Map()
    for (const target of targets ?? []) {
      if (target?.tier && target?.id && target.tier !== 'enterprise') {
        seen.set(`${target.tier}:${target.id}`, { tier: target.tier, id: target.id })
      }
    }
    return [...seen.values()]
  }, [targets])

  const results = useQueries({
    queries: unique.map((target) => ({
      queryKey: [...queryKeys.hierarchy, 'path', target.tier, target.id],
      queryFn: () => fetchPath(client, target),
      enabled,
      staleTime: NAME_STALE_MS,
    })),
  })

  /* `results` is a new array every render; a signature of what each one holds
     is the stable thing to memoise on — and it has one length, where spreading
     the results into the deps would change the array's size between renders. */
  const signature = results.map((result) => `${result.status}:${result.dataUpdatedAt}`).join('|')

  return useMemo(() => {
    const byId = new Map()
    const resolving = new Set()
    results.forEach((result, index) => {
      if (result.isPending) resolving.add(unique[index].id)
      for (const node of result.data ?? []) byId.set(node.id, node)
    })
    const failed = results.find((result) => result.isError)
    return {
      data: { items: [...byId.values()] },
      resolving,
      isPending: enabled && results.some((result) => result.isPending),
      isError: Boolean(failed),
      error: failed?.error ?? null,
      refetch: () => Promise.all(results.map((result) => result.refetch())),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` stands for `results`
  }, [enabled, unique, signature])
}

/** Names change rarely, and a rename invalidates `queryKeys.hierarchy` anyway. */
const NAME_STALE_MS = 5 * 60_000

/** A node and its ancestors, outermost first — `[]` when the node is gone. */
async function fetchPath(client, target) {
  const path = []
  let cursor = target
  while (cursor) {
    let node
    try {
      node = await client.fetchQuery({
        queryKey: [...queryKeys.hierarchy, 'name', cursor.tier, cursor.id],
        queryFn: ({ signal }) => fetchNode(cursor, signal),
        staleTime: NAME_STALE_MS,
      })
    } catch (error) {
      /* The target itself gone is "unresolved"; an ancestor gone just ends the
         trail early. Anything else is a real failure. */
      if (error?.code === ERROR_CODES.NOT_FOUND) break
      throw error
    }
    path.unshift(node)
    const parentTier = PARENT_TIER[node.tier]
    cursor = parentTier && node.parentId ? { tier: parentTier, id: node.parentId } : null
  }
  return path
}

const PARENT_TIER = { asset: 'line', line: 'site' }

/**
 * One generation, under one parent — what the drill-down renders.
 *
 * `placeholderData` holds the previous rows on screen while the next answer
 * loads, so paging and typing in the filter do not blank the list between
 * answers — WITHIN one level.
 *
 * Across levels it must not. Drilling from a site into a line with the previous
 * rows held would show that site's lines under the line's own heading until the
 * real answer arrived: not a flicker but a wrong list, briefly indistinguishable
 * from a right one. So the previous query's key is checked, and its rows are
 * kept only when the tier and the parent are the same ones.
 *
 * Keyed on the search term as well as the parent and page, because the search is
 * the server's: two different terms are two different results and must not share
 * a cache entry.
 */
export function useHierarchyChildren({ tier, parentId, search, page, pageSize }) {
  return useQuery({
    queryKey: queryKeys.hierarchyChildren(tier, parentId, search, page, pageSize),
    queryFn: ({ signal }) => fetchChildren({ tier, parentId, search, page, pageSize }, signal),
    enabled: Boolean(tier),
    placeholderData: (previous, previousQuery) => {
      // ['hierarchy', 'children', tier, parentId, search, page, pageSize]
      const key = previousQuery?.queryKey
      const sameLevel = key?.[2] === tier && key?.[3] === (parentId ?? null)
      return sameLevel ? previous : undefined
    },
    staleTime: 30_000,
  })
}

/**
 * The path down to a node, for a cold arrival.
 *
 * Disabled while drilling: the screen already holds every node it walked
 * through, so this runs only when a link or a reload lands somewhere with no
 * trail in hand. `staleTime: Infinity` because an ancestor chain changes only
 * when the hierarchy is restructured, and the mutations below invalidate it.
 */
export function useHierarchyAncestors(target, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.hierarchyAncestors(target?.tier, target?.id),
    queryFn: ({ signal }) => fetchAncestors(target, signal),
    enabled: enabled && Boolean(target?.tier && target?.id),
    staleTime: Infinity,
  })
}

/**
 * How many children one node has.
 *
 * Read when an editor opens, for the removal warning — which has to state a
 * real number, and cannot count from a screen showing one generation. Never
 * cached: it is read at the moment it is about to be acted on.
 */
export function useChildCount(target) {
  return useQuery({
    queryKey: queryKeys.hierarchyChildCount(target?.tier, target?.id),
    queryFn: ({ signal }) => fetchChildCount(target, signal),
    enabled: Boolean(target?.tier && target?.id),
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * One node, with the fields the tree endpoint does not serve.
 *
 * `/assets/hierarchy` carries id, name and short code. An edit form needs
 * `is_active` and `asset_class_id` as well, and every update here is a full
 * replace — so a form prefilled from the tree alone would switch the record off
 * and unassign its class on save. This reads the record being overwritten.
 *
 * `staleTime: 0, gcTime: 0` for the reason `useConnection` gives: an edit form is
 * the one place a stale prefill becomes a wrong write.
 */
export function useHierarchyNode(target) {
  return useQuery({
    queryKey: queryKeys.hierarchyNode(target?.tier, target?.id),
    queryFn: ({ signal }) => fetchNode(target, signal),
    enabled: Boolean(target?.tier && target?.id),
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * Anything that changes the hierarchy invalidates all of it.
 *
 * `queryKeys.hierarchy` rather than `hierarchyNodes`: the per-level lists, the
 * ancestor chains and the child counts all live under that prefix now, and a
 * rename that left a stale name in the list behind the form would read as a
 * failed write. Only what is mounted actually refetches, so the breadth costs
 * little — a create refreshes the one list it landed in, not five.
 *
 * The whole-tree read is under the same prefix and goes too, which is what keeps
 * the scope selector in the top bar from showing a node that was just removed.
 *
 * The server assigns the id and is the only thing that knows whether a write
 * landed, so nothing here guesses at the result.
 */
function useHierarchyMutation(mutationFn) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.hierarchy }),
  })
}

export const useCreateNode = () => useHierarchyMutation(createNode)
export const useUpdateNode = () => useHierarchyMutation(updateNode)
export const useDeleteNode = () => useHierarchyMutation(deleteNode)
